import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { ROUTES } from 'configs';
import {
  MainMenuItemWrapper,
  MainMenuItem,
  DefaultCommunityImage,
  PendingSettingsInvite,
  Tooltip,
} from 'components';
import { INFO_POPUP_V2 } from 'constants/popups';
import { SUBSCRIPTION_OVER_POPUP } from 'constants/sidebars';
import { openModal, closeModal } from 'store/actionCreators/modal';
import { CommunityRoles } from 'types';
import type { MainMenuItemDisableReason } from '../MainMenuItemWrapper';

import * as S from './styled';

export type MainMenuCommunityDisableReason = MainMenuItemDisableReason;

interface MainMenuCommunityItemProps {
  id: string;
  index?: number;
  isShowTooltip: boolean;
  title: string;
  image: string | null;
  disableReason?: MainMenuCommunityDisableReason;
  invitesCount: number;
  userRole: CommunityRoles;
}

function PauseGlyph() {
  return (
    <svg width="8" height="8" viewBox="0 0 8 8" aria-hidden>
      <rect x="0.5" y="1" width="2.2" height="6" rx="0.5" fill="currentColor" />
      <rect x="5.3" y="1" width="2.2" height="6" rx="0.5" fill="currentColor" />
    </svg>
  );
}

function BlockedGlyph() {
  return (
    <svg width="9" height="9" viewBox="0 0 12 12" fill="none" aria-hidden>
      <circle cx="6" cy="6" r="4.25" stroke="currentColor" strokeWidth="1.5" />
      <line
        x1="3"
        y1="3"
        x2="9"
        y2="9"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

const MainMenuCommunityItem: FC<MainMenuCommunityItemProps> = ({
  isShowTooltip,
  title,
  image,
  id,
  disableReason: disableReasonProp = 'none',
  invitesCount,
  userRole,
  index,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { communityId } = useParams();
  const isSelected = communityId === id;
  const marginTop = index === 0 && invitesCount > 0;

  const disableReason = disableReasonProp;
  const isDisabled = disableReason !== 'none';

  const sidebarAriaHint =
    disableReason === 'blocked'
      ? t('community.sidebarCommunityBlockedHint')
      : disableReason === 'subscriptionInactive'
      ? t(
          'community.sidebarCommunitySubscriptionInactiveHint',
          'Subscription expired; click for details',
        )
      : disableReason === 'subscriptionSlotInactive'
      ? t('community.sidebarCommunityPausedHint')
      : '';

  const linkAriaLabel =
    isDisabled && sidebarAriaHint ? `${title}. ${sidebarAriaHint}` : title;

  const onDisabledClick = () => {
    if (disableReason === 'blocked') {
      dispatch(
        openModal({
          type: INFO_POPUP_V2,
          data: {
            headerText: t('community.userBlockedTitle'),
            messageText: t('community.userBlockedText'),
            defaultButtonText: 'Ok',
            type: 'warning',
            withoutCloseButton: true,
            onConfirm: () => {
              dispatch(closeModal());
            },
          },
        }),
      );
      return;
    }

    if (disableReason === 'subscriptionInactive') {
      dispatch(
        openModal({
          type: SUBSCRIPTION_OVER_POPUP,
          data: {
            variant: 'expired',
            communityId: id,
          },
        }),
      );
      return;
    }

    dispatch(
      openModal({
        type: INFO_POPUP_V2,
        data: {
          headerText: t('community.subscriptionSlotInactiveTitle'),
          messageText: t('community.subscriptionSlotInactiveText'),
          defaultButtonText: 'Ok',
          type: 'warning',
          withoutCloseButton: true,
          onConfirm: () => {
            dispatch(closeModal());
          },
        },
      }),
    );
  };

  return (
    <Tooltip
      isVisible={isShowTooltip}
      text={
        isDisabled && sidebarAriaHint ? `${title} - ${sidebarAriaHint}` : title
      }
      position="right-center"
    >
      <S.ItemSlot
        $isSelected={isSelected && !isDisabled}
        style={marginTop ? { marginTop: '8px' } : undefined}
        onClick={isDisabled ? onDisabledClick : () => {}}
      >
        <MainMenuItemWrapper
          to={`${ROUTES.COMMUNITY}/${id}/overall`}
          isDisabled={isDisabled}
          isSelected={isSelected && !isDisabled}
          disableReason={disableReason}
          aria-label={linkAriaLabel}
        >
          {invitesCount > 0 &&
            (userRole === CommunityRoles.ADMIN ||
              userRole === CommunityRoles.OWNER) && (
              <PendingSettingsInvite
                invitesCount={invitesCount}
                addCss={{ top: -2, right: 0 }}
              />
            )}
          <S.AvatarStack $dimmed={isDisabled}>
            {image ? (
              <MainMenuItem image={image} />
            ) : (
              <DefaultCommunityImage title={title.charAt(0).toUpperCase()} />
            )}
          </S.AvatarStack>
          {isDisabled ? (
            <S.StateBadge
              $variant={disableReason}
              aria-hidden
              title={
                disableReason === 'blocked'
                  ? t('community.sidebarCommunityBlockedHint')
                  : t('community.sidebarCommunityPausedHint')
              }
            >
              {disableReason === 'blocked' ? <BlockedGlyph /> : <PauseGlyph />}
            </S.StateBadge>
          ) : null}
        </MainMenuItemWrapper>
      </S.ItemSlot>
    </Tooltip>
  );
};

export default MainMenuCommunityItem;
